import { useRef, useState } from "react";
import { FileCode, Type, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { EmailFrame, mergeSubject } from "./emailPreview";
import { LogoBar } from "./LogoBar";

const TAGS = ["name", "first_name", "company", "email", "title", "logo"];

function Segmented({ value, options, onChange, prefix }) {
  return (
    <div className="flex rounded-lg border border-border p-0.5 bg-background">
      {options.map(([v, label, Icon]) => (
        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${prefix}-${v}`}
          className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors ${value === v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
          {Icon && <Icon className="h-3.5 w-3.5" />}{label}
        </button>
      ))}
    </div>
  );
}

export function TemplateEditor({ form, setForm, slot, project, onProjectChange }) {
  const bodyRef = useRef(null);
  const fileRef = useRef(null);
  const imgRef = useRef(null);
  const [mode, setMode] = useState("edit");
  const isHtml = form.format === "html";
  const looksHtml = (b) => /^\s*(<!doctype\s+html|<html\b|<(table|div|p|h[1-6]|center|body)\b)/i.test(b) || /<!doctype\s+html|<html\b/i.test(b);
  const setBody = (body) => setForm({ ...form, body, format: !isHtml && looksHtml(body) ? "html" : form.format });
  const hasCid = isHtml && /src=["']?cid:/i.test(form.body);
  const hasMime = /^Content-Type:|^--[\w=.-]{10,}\s*$/im.test(form.body);

  const insertText = (token) => {
    const el = bodyRef.current;
    const pos = el ? el.selectionStart : form.body.length;
    setForm({ ...form, body: form.body.slice(0, pos) + token + form.body.slice(pos) });
    setTimeout(() => { el?.focus(); el?.setSelectionRange(pos + token.length, pos + token.length); }, 0);
  };
  const insert = (tag) => insertText(`{${tag}}`);

  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/projects/${slot}/images`, fd);
      insertText(`<img src="${data.url}" alt="" style="max-width:100%;height:auto;border:0">`);
      toast.success("Image uploaded and inserted");
    } catch (e) { toast.error(errMsg(e)); } finally { imgRef.current.value = ""; }
  };

  const importHtml = async (file) => {
    if (!file) return;
    const body = await file.text();
    setForm({ ...form, body, format: "html", name: form.name || file.name.replace(/\.html?$/i, "") });
    fileRef.current.value = "";
  };

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label>Template name</Label>
          <Input value={form.name} placeholder="e.g. Intro offer – Q3" onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="template-name-input" /></div>
        <div className="space-y-1.5"><Label>Subject</Label>
          <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="template-subject-input" /></div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Segmented prefix="template-format" value={form.format} onChange={(v) => setForm({ ...form, format: v })}
          options={[["text", "Plain text", Type], ["html", "HTML code", FileCode]]} />
        {isHtml && (
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="template-import-html-button"
            className="text-xs px-3 py-1.5 rounded-md border border-border bg-card hover:bg-secondary flex items-center gap-1.5">
            <Upload className="h-3.5 w-3.5" />Import .html file
          </button>
        )}
        {isHtml && (
          <button type="button" onClick={() => imgRef.current?.click()} data-testid="template-upload-image-button"
            className="text-xs px-3 py-1.5 rounded-md border border-border bg-card hover:bg-secondary flex items-center gap-1.5">
            <ImageIcon className="h-3.5 w-3.5" />Upload image
          </button>
        )}
        <input ref={imgRef} type="file" accept=".png,.jpg,.jpeg,.gif,.webp" className="hidden" data-testid="template-upload-image-input" onChange={(e) => uploadImage(e.target.files[0])} />
        <input ref={fileRef} type="file" accept=".html,.htm" className="hidden" data-testid="template-import-html-input" onChange={(e) => importHtml(e.target.files[0])} />
        <div className="ml-auto"><Segmented prefix="template-mode" value={mode} onChange={setMode} options={[["edit", "Edit"], ["preview", "Preview"]]} /></div>
      </div>
      <LogoBar slot={slot} project={project} onChange={onProjectChange} onInsert={mode === "edit" ? () => insert("logo") : null} />
      <div className="flex items-center gap-2 flex-wrap"><span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mr-1">Insert</span>
        {TAGS.map((t) => (
          <button key={t} type="button" onClick={() => insert(t)} data-testid={`template-merge-tag-${t}`} disabled={mode === "preview"}
            className="font-mono text-xs px-2.5 py-1 rounded-md border border-primary/30 bg-primary/10 text-indigo-700 hover:bg-primary/20 transition-colors disabled:opacity-40">{`{${t}}`}</button>
        ))}
      </div>
      {mode === "preview" ? (
        <div className="space-y-2" data-testid="template-preview">
          <p className="text-xs text-muted-foreground">Subject: <strong className="text-foreground">{mergeSubject(form.subject)}</strong> · sample data shown</p>
          <EmailFrame body={form.body} format={form.format} logoUrl={project.logo_url} testId="template-preview-frame" />
        </div>
      ) : (
        <Textarea ref={bodyRef} rows={isHtml ? 18 : 14} value={form.body} onChange={(e) => setBody(e.target.value)}
          spellCheck={!isHtml}
          placeholder={isHtml ? '<table width="100%">\n  <tr><td style="padding:24px">\n    <h1>Hi {first_name}</h1>\n    <p>Quick idea for {company}…</p>\n  </td></tr>\n</table>' : "Hi {first_name},\n\nI noticed {company} is…"}
          className={`font-mono text-sm leading-relaxed ${isHtml ? "bg-[#1f1d1a] text-[#e8e2d4] border-[#1f1d1a]" : ""}`} data-testid="template-body-textarea" />
      )}
      {hasMime && (
        <p className="text-xs rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-800 px-3 py-2" data-testid="template-mime-warning">
          This looks like a raw email file (MIME headers). On save, only the HTML document is kept and the rest is removed.
        </p>
      )}
      {hasCid && (
        <p className="text-xs rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-800 px-3 py-2" data-testid="template-cid-warning">
          Images using "cid:" (embedded attachments) will not show. Use "Upload image" to host your images and replace the cid: sources, or use {"{logo}"} for the company logo.
        </p>
      )}
      {isHtml && <p className="text-xs text-muted-foreground">Use inline CSS and table layouts for best email-client support. Footer and unsubscribe link are appended automatically.</p>}
    </div>
  );
}
